import React, { useMemo } from 'react';
import { EncounterStatus, ENCOUNTER_STATUSES } from '../../types/enums';
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  Notes as NotesIcon,
  Favorite as VitalsIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  MedicalServices as DiagnosisIcon,
} from '@mui/icons-material';
import EnhancedTextEditor from '../common/EnhancedTextEditor';
import LocationInformationPanel from './LocationInformationPanel';
import SymptomsDropdown from './SymptomsDropdown';
import AllergySelectorDialog from '../patients/AllergySelectorDialog';
import QuickListIntegrated from '../orders/QuickListIntegrated';
import DiagnosisCodeSelector from '../common/DiagnosisCodeSelector';
import OncologyEpisodeLink from './OncologyEpisodeLink';

// Styled Components
const CompactSection = ({ children, sx, ...props }) => (
  <Box
    sx={{
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: '6px',
      mb: 0.5,
      '&:last-child': { mb: 0 },
      ...sx,  // Merge additional sx props instead of replacing
    }}
    {...props}
  >
    {children}
  </Box>
);

const SectionHeader = ({ children, ...props }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 600,
      fontSize: '1rem',
      color: 'primary.main',
      padding: '8px 12px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#fafafa',
      m: 0,
    }}
    {...props}
  >
    {children}
  </Typography>
);

const SectionHeaderWithIcon = ({ icon: Icon, children, ...props }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      fontWeight: 600,
      fontSize: '1rem',
      color: 'primary.main',
      padding: '8px 12px',
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'grey.50',
      margin: 0,
    }}
    {...props}
  >
    {Icon && <Icon sx={{ fontSize: 20 }} />}
    {children}
  </Box>
);

const SectionContent = ({ children, sx, ...props }) => (
  <Box sx={{ padding: '8px 12px', ...sx }} {...props}>
    {children}
  </Box>
);

// Helper function to safely extract vital sign values
// Handles both string values and object values with 'value' property
const getVitalValue = (vital) => {
  if (vital === null || vital === undefined) return '';
  if (typeof vital === 'string' || typeof vital === 'number') return String(vital);
  if (typeof vital === 'object' && vital.value !== undefined) return String(vital.value);
  return '';
};

const ClinicalDataTab = ({
  encounter,
  patient,
  formData,
  setFormData,
  mode = 'edit',
  encounterData,
  onOpenAllergiesDialog,
  onOpenQuickListDialog,
}) => {
  const {
    doctors = [],
    attendingProvider,
  } = encounterData || {};

  // Memoize bedInfo to avoid unnecessary re-renders
  const bedInfo = useMemo(() => ({
    bed: formData?.bed,
    bedId: formData?.bedId,
    bedName: formData?.bedName,
    inpatientBedNumber: formData?.inpatientBedNumber,
    observationBedNumber: formData?.observationBedNumber,
    department: formData?.department,
    departmentId: formData?.departmentId,
    departmentName: formData?.departmentName,
    room: formData?.room,
    roomId: formData?.roomId,
    roomName: formData?.roomName,
    // Add any other relevant fields here
  }), [
    formData?.bed,
    formData?.bedId,
    formData?.bedName,
    formData?.inpatientBedNumber,
    formData?.observationBedNumber,
    formData?.department,
    formData?.departmentId,
    formData?.departmentName,
    formData?.room,
    formData?.roomId,
    formData?.roomName,
  ]);



  // Note: Location validation is now handled by LocationInformationPanel component

  return (
    <Grid container spacing={2}>
      {/* Left Column - Clinical Information */}
      <Grid item xs={12} lg={8}>
        {/* Location and Provider Information Row */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={12} md={7}>
            <LocationInformationPanel
              bedInfo={bedInfo}
              encounterId={encounter?.id}
              onLocationUpdate={(locationData) => {
                // Update form data with new location information
                setFormData(prev => ({
                  ...prev,
                  department: locationData.departmentId,
                  departmentId: locationData.departmentId,
                  departmentName: locationData.departmentId, // Will be resolved to name by the component
                  room: locationData.roomId,
                  roomId: locationData.roomId,
                  roomName: locationData.roomId, // Will be resolved to name by the component
                  bed: locationData.bedId,
                  bedId: locationData.bedId,
                  bedName: locationData.bedId, // Will be resolved to name by the component
                }));
              }}
              disabled={mode === 'view'}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CompactSection sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <SectionHeaderWithIcon icon={PersonIcon}>Attending Physician</SectionHeaderWithIcon>
              <SectionContent sx={{ py: 1, flex: 1, display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' }}>Select Attending</InputLabel>
                  <Select
                    value={(() => {
                      const currentValue = formData?.attendingPhysician || attendingProvider?.id || '';
                      const isValidValue = doctors.some(doctor => doctor.id === currentValue);
                      return isValidValue ? currentValue : '';
                    })()}
                    label="Select Attending"
                    disabled={mode === 'view'}
                    sx={{
                      '& .MuiSelect-select': {
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }
                    }}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedProvider = doctors.find(p => p.id === selectedId);
                      
                      setFormData(prev => ({
                        ...prev,
                        attendingPhysician: selectedId,
                        attendingPhysicianName: selectedProvider?.name || ''
                      }));
                    }}
                  >
                    <MenuItem 
                      value=""
                      sx={{
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      <em>No attending assigned</em>
                    </MenuItem>
                    {doctors.map(doctor => {
                      let roleDisplay = '';
                      if (doctor.role) {
                        roleDisplay = doctor.role.replace(/_/g, ' ');
                      } else if (doctor.roles && Array.isArray(doctor.roles)) {
                        roleDisplay = doctor.roles.map(role => role.replace(/_/g, ' ')).join(', ');
                      }
                      
                      return (
                        <MenuItem 
                          key={doctor.id} 
                          value={doctor.id}
                          sx={{
                            fontFamily: 'Roboto, Arial, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {doctor.name} {roleDisplay && `(${roleDisplay})`}
                          {doctor.specialties?.length > 0 && (
                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              - {doctor.specialties.join(', ')}
                            </Typography>
                          )}
                        </MenuItem>
                      );
                    })}
                    {attendingProvider?.id && !doctors.some(doctor => doctor.id === attendingProvider.id) && (
                      <MenuItem 
                        value={attendingProvider.id}
                        sx={{
                          fontFamily: 'Roboto, Arial, sans-serif',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                        }}
                      >
                        {attendingProvider.name || attendingProvider.id} (Care Team)
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </SectionContent>
            </CompactSection>
          </Grid>
        </Grid>

        {/* All Notes in One Section - No Tabs */}
        <CompactSection>
          <SectionHeaderWithIcon icon={NotesIcon}>Clinical Notes</SectionHeaderWithIcon>
          <SectionContent>
            <Grid container spacing={2}>
              {/* Consultation Notes */}
              <Grid item xs={12} md={6}>
                <EnhancedTextEditor
                  label="Consultation Notes"
                  value={formData?.notes?.consultationNotes || ''}
                  onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, consultationNotes: e.target.value } }))}
                  placeholder="Enter consultation notes... Type /m for macros or @code to expand"
                  disabled={mode === 'view'}
                  height="120px"
                  multiline
                  rows={4}
                  fullWidth
                />
              </Grid>
              
              {/* Progress Notes */}
              <Grid item xs={12} md={6}>
                <EnhancedTextEditor
                  label="Progress Notes"
                  value={formData?.notes?.progressNotes || ''}
                  onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, progressNotes: e.target.value } }))}
                  placeholder="Enter progress notes... Type /m for macros or @code to expand"
                  disabled={mode === 'view'}
                  height="120px"
                  multiline
                  rows={4}
                  fullWidth
                />
              </Grid>

              {/* SOAP Notes */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>SOAP Notes</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <EnhancedTextEditor
                      label="Subjective"
                      value={formData?.notes?.soapNotes?.subjective || ''}
                      onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, soapNotes: { ...f?.notes?.soapNotes, subjective: e.target.value } } }))}
                      placeholder="Patient's subjective report... Type /m for macros or @code to expand"
                      disabled={mode === 'view'}
                      height="100px"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EnhancedTextEditor
                      label="Objective"
                      value={formData?.notes?.soapNotes?.objective || ''}
                      onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, soapNotes: { ...f?.notes?.soapNotes, objective: e.target.value } } }))}
                      placeholder="Objective findings... Type /m for macros or @code to expand"
                      disabled={mode === 'view'}
                      height="100px"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EnhancedTextEditor
                      label="Assessment"
                      value={formData?.notes?.soapNotes?.assessment || ''}
                      onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, soapNotes: { ...f?.notes?.soapNotes, assessment: e.target.value } } }))}
                      placeholder="Clinical assessment... Type /m for macros or @code to expand"
                      disabled={mode === 'view'}
                      height="100px"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EnhancedTextEditor
                      label="Plan"
                      value={formData?.notes?.soapNotes?.plan || ''}
                      onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, soapNotes: { ...f?.notes?.soapNotes, plan: e.target.value } } }))}
                      placeholder="Treatment plan... Type /m for macros or @code to expand"
                      disabled={mode === 'view'}
                      height="100px"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Other Notes */}
              <Grid item xs={12} md={4}>
                <EnhancedTextEditor
                  label="Nurse Notes"
                  value={formData?.notes?.nurseNotes || ''}
                  onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, nurseNotes: e.target.value } }))}
                  placeholder="Nursing notes... Type /m for macros or @code to expand"
                  disabled={mode === 'view'}
                  height="100px"
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <EnhancedTextEditor
                  label="Procedure Notes"
                  value={formData?.notes?.procedureNotes || ''}
                  onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, procedureNotes: e.target.value } }))}
                  placeholder="Procedure documentation... Type /m for macros or @code to expand"
                  disabled={mode === 'view'}
                  height="100px"
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <EnhancedTextEditor
                  label="Phone Notes"
                  value={formData?.notes?.phoneNotes || ''}
                  onChange={(e) => setFormData(f => ({ ...f, notes: { ...f?.notes, phoneNotes: e.target.value } }))}
                  placeholder="Phone consultation notes... Type /m for macros or @code to expand"
                  disabled={mode === 'view'}
                  height="100px"
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </SectionContent>
        </CompactSection>

        {/* Vitals Section */}
        <CompactSection>
          <SectionHeaderWithIcon icon={VitalsIcon}>Vital Signs</SectionHeaderWithIcon>
          <SectionContent>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Blood Pressure"
                  value={getVitalValue(formData?.vitals?.bloodPressure)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, bloodPressure: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Heart Rate"
                  value={getVitalValue(formData?.vitals?.heartRate)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, heartRate: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Temperature"
                  value={getVitalValue(formData?.vitals?.temperature)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, temperature: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Resp Rate"
                  value={getVitalValue(formData?.vitals?.respiratoryRate)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, respiratoryRate: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="O2 Sat"
                  value={getVitalValue(formData?.vitals?.oxygenSaturation)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, oxygenSaturation: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Weight"
                  value={getVitalValue(formData?.vitals?.weight)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, weight: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Height"
                  value={getVitalValue(formData?.vitals?.height)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, height: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="BMI"
                  value={getVitalValue(formData?.vitals?.bmi)}
                  onChange={(e) => setFormData(f => ({ ...f, vitals: { ...f?.vitals, bmi: e.target.value } }))}
                  fullWidth
                  disabled={true}
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' },
                  }}
                />
              </Grid>
            </Grid>
          </SectionContent>
        </CompactSection>
      </Grid>

      {/* Right Column - Patient Info & Actions */}
      <Grid item xs={12} lg={4}>
        {/* Status */}
        <CompactSection>
          <SectionHeaderWithIcon icon={PersonIcon}>Status</SectionHeaderWithIcon>
          <SectionContent>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' }}>Encounter Status</InputLabel>
              <Select
                value={formData?.status || EncounterStatus.IN_PROGRESS}
                label="Encounter Status"
                disabled={mode === 'view'}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                sx={{
                  '& .MuiSelect-select': {
                    fontFamily: 'Roboto, Arial, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }
                }}
              >
                {ENCOUNTER_STATUSES.map(status => (
                  <MenuItem 
                    key={status} 
                    value={status}
                    sx={{
                      fontFamily: 'Roboto, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    {status.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </SectionContent>
        </CompactSection>

        {/* Allergies */}
        <CompactSection>
          <SectionHeaderWithIcon icon={WarningIcon}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Allergies</Typography>
              {mode !== 'view' && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={onOpenAllergiesDialog}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    fontSize: '0.75rem',
                    textTransform: 'none'
                  }}
                >
                  Add
                </Button>
              )}
            </Box>
          </SectionHeaderWithIcon>
          <SectionContent>
            <Box>
              {Array.isArray(patient?.allergies) && patient.allergies.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {patient.allergies.map((allergy, index) => (
                    <Chip 
                      key={index} 
                      label={Array.isArray(allergy.name) ? allergy.name.join(', ') : String(allergy.name || allergy)} 
                      size="small" 
                      color="error" 
                      sx={{ 
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: 'error.contrastText',
                          '&:hover': {
                            color: 'error.dark',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    No known allergies
                  </Typography>
                  {mode !== 'view' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={onOpenAllergiesDialog}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        borderColor: 'primary.light',
                        color: 'primary.main',
                      }}
                    >
                      Add Allergies
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </SectionContent>
        </CompactSection>

        {/* Symptoms */}
        <CompactSection>
          <SectionHeaderWithIcon icon={WarningIcon}>Symptoms</SectionHeaderWithIcon>
          <SectionContent>
            <SymptomsDropdown
              selectedSymptoms={formData?.symptoms || []}
              onChange={(newSymptoms) => setFormData(f => ({ ...f, symptoms: newSymptoms }))}
              label="Select Symptoms"
              placeholder="Search and select symptoms..."
              disabled={mode === 'view'}
            />
          </SectionContent>
        </CompactSection>

        {/* Diagnosis Codes (ICD-10) */}
        <CompactSection>
          <SectionHeaderWithIcon icon={DiagnosisIcon}>Diagnosis Codes (ICD-10)</SectionHeaderWithIcon>
          <SectionContent>
            <DiagnosisCodeSelector
              value={formData?.diagnoses || []}
              onChange={(newDiagnoses) => setFormData(f => ({ ...f, diagnoses: newDiagnoses }))}
              encounterType={formData?.encounterType || encounter?.encounterType || 'outpatient'}
              disabled={mode === 'view'}
              required={false}
            />
          </SectionContent>
        </CompactSection>

        {/* Oncology Episode Link */}
        <OncologyEpisodeLink
          encounterId={encounter?.id}
          patientId={encounter?.patientId || patient?.id}
          linkedEpisodeId={encounter?.oncologyEpisodeId || formData?.oncologyEpisodeId}
          onLinkChange={(episodeId) => {
            setFormData(f => ({ ...f, oncologyEpisodeId: episodeId }));
          }}
          compact={true}
          readOnly={mode === 'view'}
        />

        {/* Quick Actions - Bulk Ordering */}
        <CompactSection>
          <SectionHeaderWithIcon icon={AddIcon}>Quick Actions</SectionHeaderWithIcon>
          <SectionContent>
            <QuickListIntegrated
              patientId={encounter?.patientId || patient?.id}
              onOrdersCreated={(createdOrders) => {
                console.log('Orders created from quick actions:', createdOrders);
              }}
              readOnly={mode === 'view'}
              maxHeight="325px"
              showNewOrders={false}
            />
          </SectionContent>
        </CompactSection>
      </Grid>
    </Grid>
  );
};

export default ClinicalDataTab; 